import { type ReactElement, type ReactNode } from 'react';
import { Box, Card, CardContent, FeatureErrorBoundary } from '@citibox-panel/ui';
import { PageHeader } from '../../../components/PageHeader';
import { useQrBatchStats } from '../hooks/useQrBatchStats';
import { useQrBatches } from '../hooks/useQrBatches';
import type { QrBatchSeams } from '../lib/qrBatch';
import { BatchesCard } from './BatchesCard';
import { PageIntro } from './PageIntro';
import { RequestBatchCard } from './RequestBatchCard';
import { StatsStrip } from './StatsStrip';

export interface QrBatchesPageProps extends QrBatchSeams {
  brandLabel: string;
  brandDegraded: boolean;
  headerEnd?: ReactNode;
  // The panel's own IANA zone for the dates; `null` renders in UTC.
  timeZone: string | null;
}

// Reached by direct URL and hanging off no navigation, so it carries its own chrome. Everything in
// it is filled by the host: this module resolves no config and knows no route. It opens no `main`
// landmark — the host's authenticated layout already does.
//
// The list is read HERE, once, because three cards depend on whether a batch is generating: the
// form waits, the list polls, and the stats poll with it.
export function QrBatchesPage({
  brandLabel,
  brandDegraded,
  headerEnd,
  timeZone,
  fetchQrBatches,
  fetchQrBatchStats,
  requestQrBatch,
  downloadQrBatch,
}: QrBatchesPageProps): ReactElement {
  const batches = useQrBatches(fetchQrBatches);
  const stats = useQrBatchStats(fetchQrBatchStats, batches.isGenerating);

  return (
    <>
      <PageHeader brandLabel={brandLabel} brandDegraded={brandDegraded} headerEnd={headerEnd} />
      {/* One white, rounded panel for everything the page itself says, the same surface, inset and
          padding as the other operations surfaces. */}
      <Card variant="outlined" sx={{ borderRadius: 3, m: 2 }}>
        <CardContent>
          <PageIntro />
          {/* The form first, the stats beside it, the list below: DOM order is the reading and tab
              order. Each card sits in its own boundary, so one that throws takes nothing else. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <FeatureErrorBoundary>
              <RequestBatchCard isGenerating={batches.isGenerating} requestQrBatch={requestQrBatch} />
            </FeatureErrorBoundary>
            <FeatureErrorBoundary>
              <StatsStrip
                stats={stats.stats}
                isPending={stats.isPending}
                isError={stats.isError}
                onRetry={stats.retry}
              />
            </FeatureErrorBoundary>
          </Box>
          <Box sx={{ mt: 3 }}>
            <FeatureErrorBoundary>
              <BatchesCard
                batches={batches.list?.items}
                isPending={batches.isPending}
                isError={batches.isError}
                isGenerating={batches.isGenerating}
                onRetry={batches.retry}
                timeZone={timeZone}
                downloadQrBatch={downloadQrBatch}
              />
            </FeatureErrorBoundary>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}
